import { useEffect, useMemo, useRef, useState, type DragEvent, type UIEvent } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { TimelinePlayhead } from './TimelinePlayhead';
import { TimelineRuler } from './TimelineRuler';
import { TimelineTransport } from './TimelineTransport';
import { TrackRow } from './TrackRow';
import { GraphEditor } from './GraphEditor';

const LABEL_WIDTH = 214;
const ROW_HEIGHT = 38;

export function Timeline() {
  const [graphMode, setGraphMode] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(220);
  const [scrollTop, setScrollTop] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const project = useEditorStore((state) => state.project);
  const zoom = useEditorStore((state) => state.timelineZoom);
  const addAsset = useEditorStore((state) => state.addAssetToTimeline);
  const selectLayer = useEditorStore((state) => state.selectLayer);
  const composition = project.compositions[project.activeCompositionId];
  const layers = composition.layerIds.map((id) => project.layers[id]).filter(Boolean);
  const contentWidth = Math.max(composition.duration * zoom + LABEL_WIDTH + 80, 900);
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 3);
  const endIndex = Math.min(layers.length, Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + 3);
  const visible = layers.slice(startIndex, endIndex);

  useEffect(() => {
    if (!scroller.current) return;
    const observer = new ResizeObserver((entries) => setViewportHeight(entries[0].contentRect.height));
    observer.observe(scroller.current);
    return () => observer.disconnect();
  }, []);

  const markers = useMemo(() => composition.markers, [composition.markers]);
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
    useEditorStore.getState().setTimelineScroll(event.currentTarget.scrollLeft);
  };
  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    const assetId = event.dataTransfer.getData('application/x-open-motion-asset');
    if (!assetId || !scroller.current) return;
    const rect = scroller.current.getBoundingClientRect();
    const time = Math.max(0, (event.clientX - rect.left + scroller.current.scrollLeft - LABEL_WIDTH) / zoom);
    addAsset(assetId, time);
  };

  return <section className="timeline-panel">
    <TimelineTransport graphMode={graphMode} setGraphMode={setGraphMode} />
    {graphMode ? <GraphEditor /> : <div ref={scroller} className="timeline-scroller" onScroll={onScroll} onDragOver={(event) => event.preventDefault()} onDrop={onDrop} onClick={(event) => { if ((event.target as HTMLElement).classList.contains('track-lane')) selectLayer(null); }}>
      <div className="timeline-content" style={{ width: contentWidth, height: Math.max(viewportHeight, layers.length * ROW_HEIGHT + 28) }}>
        <TimelineRuler composition={composition} zoom={zoom} labelWidth={LABEL_WIDTH} />
        <div className="marker-layer" style={{ left: LABEL_WIDTH }}>{markers.map((marker) => <div className="timeline-marker" key={marker.id} style={{ left: marker.time * zoom, color: marker.color }} title={marker.label}><i /><span>{marker.label}</span></div>)}</div>
        <div className="track-rows" style={{ top: 28, height: layers.length * ROW_HEIGHT }}>{visible.map((layer, offset) => <TrackRow key={layer.id} layer={layer} project={project} composition={composition} zoom={zoom} labelWidth={LABEL_WIDTH} index={startIndex + offset} />)}</div>
        <TimelinePlayhead zoom={zoom} labelWidth={LABEL_WIDTH} />
      </div>
    </div>}
  </section>;
}
